/** Full reload — the one way to re-resolve the tenant (main.ts resolves once
 *  at boot). Its own module so a view can be tested without touching
 *  `window.location`, which jsdom does not let a test replace. */
export function reloadPage(): void {
  window.location.reload();
}

/** How many automatic re-checks a provisioning page makes before it leaves
 *  the visitor with the manual button only — 20 × 30 s = 10 minutes, longer
 *  than any healthy provisioning run. Counted per host in sessionStorage
 *  because every reload is a fresh boot. */
export const MAX_AUTO_RETRIES = 20;
const KEY_PREFIX = "metaship:auto-retry:";

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function readAutoRetries(host: string): number {
  const raw = storage()?.getItem(KEY_PREFIX + host);
  const n = raw ? Number(raw) : 0;
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * One automatic re-check, if it is worth making: not while the tab is hidden
 * (a reload nobody sees), not while offline (it lands on the browser's error
 * page), and not past the attempt cap. Returns whether it reloaded.
 */
export function autoRetry(host: string, reload: () => void): boolean {
  if (document.hidden || navigator.onLine === false) return false;
  const attempts = readAutoRetries(host);
  if (attempts >= MAX_AUTO_RETRIES) return false;
  storage()?.setItem(KEY_PREFIX + host, String(attempts + 1));
  reload();
  return true;
}

/** A successful resolve (any status but PROVISIONING) forgets the count. */
export function clearAutoRetries(host: string): void {
  storage()?.removeItem(KEY_PREFIX + host);
}
